import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api, formatGHS, stripYear } from "@/lib/api";

export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, booking: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "unknown", booking: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", booking: data.booking });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, booking: data.booking });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", payment_status: data.payment_status, booking: data.booking });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", payment_status: null, booking: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] bg-[#f7f8fa] py-20" data-testid="booking-success-page">
      <div className="container-titan max-w-2xl">
        <div className="bg-white border border-gray-200 p-10 text-center">
          {state.status === "checking" && (
            <>
              <Loader2 className="mx-auto animate-spin text-[#e63946]" size={48} />
              <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-6">Confirming your payment…</h1>
              <p className="text-gray-600 mt-3">Hang tight — we&apos;re verifying with Stripe.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="mx-auto text-green-600" size={56} />
              <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-6">Booking confirmed!</h1>
              <p className="text-gray-600 mt-3">A confirmation email is on the way to {state.booking?.customer_email}.</p>
              {state.booking && (
                <div className="mt-6 border border-gray-200 p-5 text-left text-sm bg-[#f7f8fa]">
                  <Row label="Reference" value={state.booking.reference} />
                  <Row label="Car" value={stripYear(state.booking.car_name)} />
                  <Row label="Pickup" value={`${state.booking.pickup_location} · ${state.booking.pickup_date}`} />
                  <Row label="Return" value={`${state.booking.return_location} · ${state.booking.return_date}`} />
                  <Row label="Days" value={state.booking.days} />
                  <Row label="Total paid" value={formatGHS(state.booking.total_amount)} bold />
                </div>
              )}
              <Link to="/" className="btn-primary mt-8 inline-flex" data-testid="success-home-btn">Back home <ArrowRight size={14} /></Link>
            </>
          )}
          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <>
              <XCircle className="mx-auto text-[#e63946]" size={56} />
              <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-6">
                {state.status === "expired" ? "Payment session expired" : state.status === "timeout" ? "Still processing…" : "Something went wrong"}
              </h1>
              <p className="text-gray-600 mt-3">
                If you completed payment, please give it a few minutes and check your reference, or contact us.
              </p>
              <Link to="/fleet" className="btn-outline-navy mt-8 inline-flex">Browse fleet again</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-gray-500 text-xs uppercase tracking-widest">{label}</span>
      <span className={`text-[#0d2040] ${bold ? "font-display text-lg font-bold text-[#e63946]" : ""}`}>{value}</span>
    </div>
  );
}
